import React, { useState, useMemo, useEffect, useRef } from 'react';
import Navbar from '../../Navbar/nbar';
import { Link } from 'react-router-dom';
import Footer from '../../footer/Footer';
import './individual.css';
import videoFile1 from '../../../assets/featvid/adv.mp4';
import videoFile2 from '../../../assets/featvid/privat.mp4';
import videoFile3 from '../../../assets/featvid/simple.mp4';

/* ═══════════════════════════════════════════
   HELPERS
   ═══════════════════════════════════════════ */
const fmt = n => '₹' + Math.round(n).toLocaleString('en-IN');

function AnimCounter({ end, duration = 1200, prefix = '', suffix = '' }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !started.current) {
        started.current = true;
        const t0 = Date.now();
        const tick = () => {
          const p = Math.min((Date.now() - t0) / duration, 1);
          setVal(Math.round(end * p));
          if (p < 1) requestAnimationFrame(tick);
        };
        tick();
      }
    }, { threshold: 0.3 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [end, duration]);
  return <span ref={ref}>{prefix}{val.toLocaleString('en-IN')}{suffix}</span>;
}

/* Scroll reveal hook */
function useReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

function Reveal({ children, delay = 0, className = '' }) {
  const [ref, vis] = useReveal();
  return (
    <div ref={ref} className={`ind__reveal${vis ? ' ind__reveal--vis' : ''}${className ? ' ' + className : ''}`}
      style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

/* Ring chart */
function Ring({ value, max = 100, size = 120, stroke = 10, color = '#4f297e', children }) {
  const r = (size - stroke) / 2, circ = 2 * Math.PI * r;
  const offset = circ * (1 - Math.min(value / max, 1));
  return (
    <div className="ind__ring" style={{ width: size, height: size }}>
      <svg viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#EBEBF0" strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke}
          strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`} style={{ transition: 'stroke-dashoffset 0.8s ease' }} />
      </svg>
      <div className="ind__ring-inner">{children}</div>
    </div>
  );
}

/* Donut */
function Donut({ segments, size = 150, stroke = 18 }) {
  const r = (size - stroke) / 2, circ = 2 * Math.PI * r;
  let acc = 0;
  return (
    <svg viewBox={`0 0 ${size} ${size}`} style={{ width: size, height: size }}>
      {segments.map((seg, i) => {
        const len = circ * seg.pct / 100, gap = segments.length > 1 ? 2 : 0;
        const rot = -90 + acc * 3.6; acc += seg.pct;
        return <circle key={i} cx={size / 2} cy={size / 2} r={r} fill="none" stroke={seg.color} strokeWidth={stroke}
          strokeDasharray={`${len - gap} ${circ - len + gap}`} strokeLinecap="round"
          transform={`rotate(${rot} ${size / 2} ${size / 2})`} style={{ transition: 'stroke-dashoffset 0.6s ease, stroke-dasharray 0.6s ease' }} />;
      })}
    </svg>
  );
}

/* Stack bar */
function StackBar({ segments }) {
  return (
    <div className="ind__stackbar">
      {segments.map((s, i) => (
        <div key={i} className="ind__stackbar-seg" style={{ width: `${s.pct}%`, background: s.color }} title={s.label}>
          {s.pct > 8 && <span>{s.pct.toFixed(0)}%</span>}
        </div>
      ))}
    </div>
  );
}

/* Scroll progress bar */
function ScrollProgress() {
  const [pct, setPct] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement.scrollHeight - window.innerHeight;
      setPct(h > 0 ? (window.scrollY / h) * 100 : 0);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return <div className="ind__scroll-bar" style={{ width: `${pct}%` }} />;
}

/* ═══════════════════════════════════════════
   DATA
   ═══════════════════════════════════════════ */
const STATS = [
  { value: 15000, suffix: '+', label: 'Active Users' },
  { value: 850, prefix: '₹', suffix: ' Cr+', label: 'Assets Tracked' },
  { value: 98, suffix: '%', label: 'User Satisfaction' },
  { value: 4.8, suffix: '/5', label: 'App Rating', decimals: true },
];

const ARTICLES = [
  { tag: 'Budgeting', title: 'The 50-30-20 Rule That Actually Works', desc: 'Allocate 50% to needs, 30% to wants, and 20% to savings. This framework builds a sustainable spending plan without spreadsheet overload.', read: '4 min', accent: '#4f297e' },
  { tag: 'Investing', title: 'Why SIP Beats Lump Sum for Most People', desc: 'Systematic Investment Plans remove emotion from investing. Rupee-cost averaging means you buy more units when markets dip — automatically.', read: '5 min', accent: '#1A8A63' },
  { tag: 'Tax Planning', title: 'Section 80C: Beyond the Basics', desc: 'ELSS, PPF, NPS — most people stop at ₹1.5L. Learn about 80CCD(1B), 80D, and salary restructuring to save ₹3L more in taxes.', read: '6 min', accent: '#A67C00' },
  { tag: 'Emergency Fund', title: 'The ₹0 to ₹3 Lakh Playbook', desc: 'Start with ₹5,000/month into a liquid fund. In 3 years you have a safety net that protects every other financial goal.', read: '3 min', accent: '#4f297e' },
  { tag: 'Debt', title: 'Credit Card Debt: The 42% Trap', desc: 'Paying minimum due means 36–42% annual interest. The avalanche method eliminates high-interest debt fastest and saves lakhs.', read: '4 min', accent: '#C0392B' },
  { tag: 'Insurance', title: 'Term vs Endowment: ₹10L Difference', desc: '₹1 Cr term plan costs ₹12,000/year. Same endowment costs ₹3.5L/year. Invest the difference for 5x better returns.', read: '5 min', accent: '#1A8A63' },
];

const TIPS = [
  { num: '01', title: 'Pay Yourself First', desc: 'Automate savings on salary day — not month-end. What gets automated gets done.' },
  { num: '02', title: 'The 24-Hour Rule', desc: 'Wait 24 hours before any non-essential purchase over ₹1,000. Eliminates 70% of impulse buys.' },
  { num: '03', title: 'Direct Plans Only', desc: 'Regular mutual fund plans charge 0.5–1% more. Over 20 years, that is lakhs lost to commissions.' },
  { num: '04', title: 'CIBIL Above 750', desc: 'A score above 750 saves 1–2% on loan interest. On a ₹50L home loan, that is ₹8–15L saved.' },
  { num: '05', title: 'Step-Up Your SIP', desc: 'Increase SIP by 10% each year. This single habit triples your final corpus versus flat SIPs.' },
  { num: '06', title: 'Health Cover Before 30', desc: 'Buying health insurance at 25 costs 40–50% less than at 45. Lock in low premiums early.' },
];

/* ═══════════════════════════════════════════
   SECTION 2: BUDGET TOOL
   ═══════════════════════════════════════════ */
function BudgetTool() {
  const [income, setIncome] = useState(50000);
  const [rent, setRent] = useState(12000);
  const [food, setFood] = useState(8000);
  const [transport, setTransport] = useState(3000);
  const [utilities, setUtilities] = useState(2500);
  const [emi, setEmi] = useState(5000);
  const [lifestyle, setLifestyle] = useState(6000);
  const [insurance, setInsurance] = useState(1500);
  const [investments, setInvestments] = useState(5000);

  const totalExp = rent + food + transport + utilities + emi + lifestyle + insurance + investments;
  const surplus = income - totalExp;
  const savingsRatio = income > 0 ? ((surplus + investments) / income * 100) : 0;
  const debtRatio = income > 0 ? (emi / income * 100) : 0;
  const needsTotal = rent + food + transport + utilities + emi + insurance;
  const wantsTotal = lifestyle;
  const savingsTotal = investments + Math.max(surplus, 0);
  const needsPct = income > 0 ? needsTotal / income * 100 : 0;
  const wantsPct = income > 0 ? wantsTotal / income * 100 : 0;
  const savingsPct = income > 0 ? savingsTotal / income * 100 : 0;

  const score = useMemo(() => {
    let s = 0;
    s += savingsRatio >= 30 ? 35 : savingsRatio >= 20 ? 25 : savingsRatio >= 10 ? 15 : 5;
    s += debtRatio <= 20 ? 25 : debtRatio <= 36 ? 18 : debtRatio <= 50 ? 8 : 0;
    if (insurance > 0) s += 10;
    if (investments > 0) s += 15;
    if (surplus >= 0) s += 15;
    return Math.min(s, 100);
  }, [savingsRatio, debtRatio, insurance, investments, surplus]);

  const scoreColor = score >= 75 ? '#1A8A63' : score >= 50 ? '#A67C00' : '#C0392B';
  const scoreLabel = score >= 75 ? 'Healthy' : score >= 50 ? 'Fair' : 'At Risk';

  const donutSegs = [
    { pct: Math.max(needsPct, 0), color: '#4f297e' },
    { pct: Math.max(wantsPct, 0), color: '#A67C00' },
    { pct: Math.max(savingsPct, 0), color: '#1A8A63' },
  ].filter(s => s.pct > 0);

  const barSegs = [
    { pct: income > 0 ? rent / income * 100 : 0, color: '#4f297e', label: 'Rent' },
    { pct: income > 0 ? food / income * 100 : 0, color: '#6b3fa3', label: 'Food' },
    { pct: income > 0 ? transport / income * 100 : 0, color: '#7C4DBA', label: 'Transport' },
    { pct: income > 0 ? utilities / income * 100 : 0, color: '#9B6FCF', label: 'Utilities' },
    { pct: income > 0 ? emi / income * 100 : 0, color: '#C0392B', label: 'EMI' },
    { pct: income > 0 ? lifestyle / income * 100 : 0, color: '#A67C00', label: 'Lifestyle' },
    { pct: income > 0 ? insurance / income * 100 : 0, color: '#555770', label: 'Insurance' },
    { pct: income > 0 ? investments / income * 100 : 0, color: '#1A8A63', label: 'SIP' },
  ].filter(s => s.pct > 0.5);

  const kpis = [
    { label: 'Savings Ratio', val: savingsRatio.toFixed(1), unit: '%', ok: savingsRatio >= 20, color: '#1A8A63', tip: '≥20% is healthy' },
    { label: 'Debt-to-Income', val: debtRatio.toFixed(1), unit: '%', ok: debtRatio <= 36, color: debtRatio <= 36 ? '#1A8A63' : '#C0392B', tip: '≤36% is safe' },
    { label: 'Needs Ratio', val: needsPct.toFixed(1), unit: '%', ok: needsPct <= 50, color: needsPct <= 50 ? '#4f297e' : '#A67C00', tip: '≤50% per 50-30-20' },
    { label: 'Monthly Surplus', val: surplus, unit: '₹', ok: surplus >= 0, color: surplus >= 0 ? '#1A8A63' : '#C0392B', tip: surplus >= 0 ? 'Positive cashflow' : 'Over budget' },
  ];

  const SR = ({ label, value, onChange, max, color }) => (
    <div className="ind__sr">
      <div className="ind__sr-head"><span>{label}</span><span className="ind__sr-val">{fmt(value)}</span></div>
      <input type="range" className="ind__slider" min={0} max={max} step={500} value={value} onChange={e => onChange(+e.target.value)} />
      <div className="ind__sr-bar"><div style={{ width: `${income > 0 ? value / income * 100 : 0}%`, background: color || '#4f297e' }} /></div>
    </div>
  );

  return (
    <section className="ind__section ind__tool-section" id="budget-tool">
      <div className="ind__inner">
        <Reveal><div className="ind__section-header">
          <span className="ind__tag">Interactive Tool</span>
          <h2 className="ind__section-title">Your Financial Snapshot</h2>
          <p className="ind__section-sub">Enter your monthly income and expenses. See your financial health score, key ratios, and where your money goes — in real time.</p>
        </div></Reveal>
        <div className="ind__tool-grid">
          <div className="ind__tool-left">
            <Reveal><div className="ind__tcard ind__tcard--income">
              <h4 className="ind__tcard-title">Monthly Income</h4>
              <SR label="Take-Home Salary" value={income} onChange={setIncome} max={500000} color="#1A8A63" />
            </div></Reveal>
            <Reveal delay={80}><div className="ind__tcard">
              <h4 className="ind__tcard-title">Expenses</h4>
              <SR label="Rent / Housing" value={rent} onChange={setRent} max={100000} />
              <SR label="Food & Groceries" value={food} onChange={setFood} max={50000} />
              <SR label="Transport" value={transport} onChange={setTransport} max={30000} />
              <SR label="Utilities & Bills" value={utilities} onChange={setUtilities} max={20000} />
              <SR label="EMI / Loan Payments" value={emi} onChange={setEmi} max={100000} color="#C0392B" />
              <SR label="Lifestyle & Shopping" value={lifestyle} onChange={setLifestyle} max={50000} color="#A67C00" />
            </div></Reveal>
            <Reveal delay={160}><div className="ind__tcard">
              <h4 className="ind__tcard-title">Protection & Growth</h4>
              <SR label="Insurance Premiums" value={insurance} onChange={setInsurance} max={20000} color="#555770" />
              <SR label="Investments / SIP" value={investments} onChange={setInvestments} max={100000} color="#1A8A63" />
            </div></Reveal>
          </div>

          <div className="ind__tool-right">
            <Reveal><div className="ind__tcard ind__tcard--hero-score">
              <Ring value={score} size={140} stroke={14} color={scoreColor}>
                <div className="ind__score-num" style={{ color: scoreColor }}>{Math.round(score)}</div>
                <div className="ind__score-lbl">{scoreLabel}</div>
              </Ring>
              <div className="ind__score-info">
                <h4>Financial Health</h4>
                <p className="ind__muted">Based on savings rate, debt load, insurance, investments, and cashflow.</p>
              </div>
            </div></Reveal>

            <Reveal delay={80}><div className="ind__kpi-row">
              {kpis.map((k, i) => (
                <div key={i} className={`ind__kpi${k.ok ? '' : ' ind__kpi--warn'}`}>
                  <div className="ind__kpi-val" style={{ color: k.color }}>
                    {k.unit === '₹' ? fmt(k.val) : <>{parseFloat(k.val).toFixed(1)}{k.unit}</>}
                  </div>
                  <div className="ind__kpi-name">{k.label}</div>
                  <div className="ind__kpi-tip">{k.tip}</div>
                </div>
              ))}
            </div></Reveal>

            <Reveal delay={160}><div className="ind__tcard">
              <h4 className="ind__tcard-title">Expense Breakdown</h4>
              <StackBar segments={barSegs} />
              <div className="ind__bar-legend">{barSegs.map((s, i) => <span key={i}><i style={{ background: s.color }} />{s.label}</span>)}</div>
            </div></Reveal>

            <Reveal delay={220}><div className="ind__tcard">
              <h4 className="ind__tcard-title">50-30-20 Analysis</h4>
              <div className="ind__split">
                <div className="ind__split-chart"><Donut segments={donutSegs} size={140} stroke={18} /></div>
                <div className="ind__split-data">
                  <div><i style={{ background: '#4f297e' }} /><span>Needs</span><strong>{needsPct.toFixed(0)}%</strong><em>{fmt(needsTotal)}</em></div>
                  <div><i style={{ background: '#A67C00' }} /><span>Wants</span><strong>{wantsPct.toFixed(0)}%</strong><em>{fmt(wantsTotal)}</em></div>
                  <div><i style={{ background: '#1A8A63' }} /><span>Savings</span><strong>{savingsPct.toFixed(0)}%</strong><em>{fmt(savingsTotal)}</em></div>
                </div>
              </div>
              {surplus < 0 && <div className="ind__alert ind__alert--danger">Spending exceeds income. Immediate action needed.</div>}
              {surplus >= 0 && savingsPct < 20 && <div className="ind__alert ind__alert--warn">Savings below 20%. Reduce lifestyle or wants spending.</div>}
              {surplus >= 0 && needsPct > 50 && <div className="ind__alert ind__alert--warn">Needs exceed 50%. Consider reducing rent or EMIs.</div>}
              {surplus >= 0 && savingsPct >= 20 && needsPct <= 50 && <div className="ind__alert ind__alert--good">Healthy position. Keep building investments.</div>}
            </div></Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════ */
const Individual = () => {
  return (
    <div className="ind__page">
      <ScrollProgress />
      <Navbar />

      {/* ─── HERO ─── */}
      <section className="ind__hero" id="top">
        <div className="ind__hero-mesh" />
        <div className="ind__hero-content">
          <Reveal>
            <span className="ind__tag">For Individuals</span>
            <h1 className="ind__hero-title">Make each penny<br />work for you.</h1>
            <p className="ind__hero-sub">Gain control over your financial future with intelligent budgeting tools, personalised insights, and expert guidance.</p>
            <div className="ind__hero-actions">
              <a href="#budget-tool" className="ind__btn-primary">Try Budget Tool <span>↓</span></a>
              <a href="/contact" className="ind__btn-outline">Get Started</a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ─── FEATURES ─── */}
      <section className="ind__features-section">
        <div className="ind__inner">
          <div className="ind__features">
            {[
              { vid: videoFile1, tag: 'Financial Decisions', title: 'Accurate & Fast', desc: 'Data-driven analysis for every financial decision you face.' },
              { vid: videoFile2, tag: 'Risk Management', title: 'Personal & Secure', desc: 'Bank-grade security with personalised risk assessment.' },
              { vid: videoFile3, tag: 'Wealth Building', title: 'Future Secured', desc: 'Compound growth strategies tailored to your life stage.' },
            ].map((f, i) => (
              <Reveal key={i} delay={i * 120}>
                <div className="ind__feature">
                  <video autoPlay muted loop playsInline className="ind__feature-vid" src={f.vid} />
                  <div className="ind__feature-body">
                    <span className="ind__feature-tag">{f.tag}</span>
                    <h3 className="ind__feature-title">{f.title}</h3>
                    <p className="ind__feature-desc">{f.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ─── TRUST BAR ─── */}
      <section className="ind__trust">
        <div className="ind__inner">
          <div className="ind__trust-grid">
            {STATS.map((s, i) => (
              <div key={i} className="ind__trust-item">
                <div className="ind__trust-val">
                  {s.decimals
                    ? <>{s.prefix || ''}{s.value}{s.suffix}</>
                    : <AnimCounter end={s.value} prefix={s.prefix || ''} suffix={s.suffix} />
                  }
                </div>
                <div className="ind__trust-label">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── BUDGET TOOL ─── */}
      <BudgetTool />

      {/* ─── ARTICLES ─── */}
      <section className="ind__section ind__articles-section" id="insights">
        <div className="ind__inner">
          <Reveal><div className="ind__section-header">
            <span className="ind__tag">Financial Literacy</span>
            <h2 className="ind__section-title">Insights That Matter</h2>
            <p className="ind__section-sub">Curated knowledge for smarter financial decisions. No jargon — just actionable guidance.</p>
          </div></Reveal>
          <div className="ind__articles-grid">
            {ARTICLES.map((a, i) => (
              <Reveal key={i} delay={i * 80}>
                <article className="ind__article" style={{ '--accent': a.accent }}>
                  <div className="ind__article-accent" />
                  <div className="ind__article-tag">{a.tag}</div>
                  <h3 className="ind__article-title">{a.title}</h3>
                  <p className="ind__article-desc">{a.desc}</p>
                  <div className="ind__article-foot">
                    <span className="ind__article-read">{a.read}</span>
                    <span className="ind__article-arrow">→</span>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ─── TIPS ─── */}
      <section className="ind__section ind__tips-section" id="management">
        <div className="ind__inner">
          <Reveal><div className="ind__section-header">
            <span className="ind__tag">Financial Management</span>
            <h2 className="ind__section-title">Six Rules That Build Wealth</h2>
            <p className="ind__section-sub">Financial freedom comes down to a handful of habits executed consistently over time.</p>
          </div></Reveal>
          <div className="ind__tips-grid">
            {TIPS.map((t, i) => (
              <Reveal key={i} delay={i * 70}>
                <div className="ind__tip">
                  <div className="ind__tip-num">{t.num}</div>
                  <h4 className="ind__tip-title">{t.title}</h4>
                  <p className="ind__tip-desc">{t.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <div className="ind__cta">
              <h3 className="ind__cta-title">Ready to take control?</h3>
              <p className="ind__muted" style={{ maxWidth: 480, margin: '0 auto 24px' }}>Start with our free financial literacy dashboard — personalised to your income, goals, and life stage.</p>
              <div className="ind__cta-btns">
                <Link to="/SMFfinlit" className="ind__btn-primary">Open Financial Dashboard <span>→</span></Link>
                <a href="/contact" className="ind__btn-outline">Speak with an Advisor</a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Individual;
